import { useCallback, useEffect, useId, useRef, useState } from 'preact/hooks';
import type { AuthMode, OnsiteProduct, OnsitePrice } from '@/types';
import { ApiError, createUpgradeQuote, confirmUpgrade, establishSession, getUpgradeStatus, confirmUpgradePayment, isUpgradeTerminal, type UpgradeSource, type UpgradeQuote, type UpgradeResult, type UpgradePaymentMethod } from '@/api/client';
import { getStripe } from '@/api/stripe-loader';
import { clearPendingUpgrade, sessionVerified, setPendingUpgrade } from '@/session';
import { getIdentityAdapter } from '@/identityAdapter';
import { createRequestScope } from '@/requestScope';
import { CheckoutShell } from '@/components/CheckoutShell';
import { useCheckoutPresentation } from '@/components/CheckoutPresentation';
import { UpgradePaymentForm, type UpgradeCardHandle } from '@/components/UpgradePaymentForm';
import { UpgradeWalletForm, type UpgradeWalletAttempt } from '@/components/UpgradeWalletForm';
import { PriceOption } from '@/components/PaywallOverlay';
import { UpgradeSummary } from '@/components/OrderSummary';
import { openSubscriberPortal } from '@/portalHandoff';
import { formatMinorUnits } from '@/money';
import { cadenceLabel } from '@/i18n/interval';
import { getLocale, hasKey, t, useT } from '@/i18n/t';
import { logError } from '@/sdkConsole';

interface Props {
  source: UpgradeSource;
  maskedEmail?: string;
  onSwitchIdentity?: (product?: OnsiteProduct, price?: OnsitePrice) => void;
  // Re-reads the upgrade options; after a resumed attempt fails, the source it
  // was opened with still says upgrade_in_progress and lists no targets.
  refreshSource: () => Promise<UpgradeSource | undefined>;
  products: OnsiteProduct[];
  initialPriceExternalId?: string;
  onSelect: (product: OnsiteProduct, price: OnsitePrice) => void;
  // product/price are the upgrade's target, resolved from the result so a
  // resumed upgrade reports what it bought.
  onSuccess: (result: UpgradeResult, product?: OnsiteProduct, price?: OnsitePrice) => void;
  onClose: () => void;
  onDismiss?: () => void;
  // The page's identity adapter, for the silent re-verify before quoting.
  authMode?: AuthMode;
  domainId?: number;
}

// Inline card replacement turns the portal-pointing decline copy into a
// prompt for the card form shown right below.
function reasonMessage(reason: string, inlinePayment: boolean): string {
  if (inlinePayment && reason === 'card_declined') {
    return t('upgrade.payment.declined');
  }
  if (inlinePayment && reason === 'payment_method_required') {
    return t('upgrade.payment.required');
  }
  const key = `upgrade.reason.${reason}`;
  return t(hasKey(key) ? key : 'upgrade.reason.generic');
}

function errorReason(err: unknown): string {
  if (!(err instanceof ApiError)) {
    return 'generic';
  }
  if (err.code) {
    return err.code;
  }
  if (err.status === 401) {
    return 'session_expired';
  }
  if (err.status === 403) {
    return 'forbidden';
  }
  return 'generic';
}

const cardBrands: Record<string, string> = {
  amex: 'American Express',
  cartes_bancaires: 'Cartes Bancaires',
  diners: 'Diners Club',
  discover: 'Discover',
  eftpos_au: 'eftpos',
  interac: 'Interac',
  jcb: 'JCB',
  mastercard: 'Mastercard',
  unionpay: 'UnionPay',
  visa: 'Visa',
};

function paymentMethodLabel(method: UpgradePaymentMethod): string {
  const brand = method.brand ? cardBrands[method.brand] : undefined;
  if (brand && method.last4) {
    return `${brand} •••• ${method.last4}`;
  }
  if (method.last4) {
    return t('upgrade.payment.endingIn', { last4: method.last4 });
  }
  return t('upgrade.payment.saved');
}

// A definitive card failure keeps the review open for a replacement card.
function isCardFailure(result: UpgradeResult): boolean {
  return result.status === 'failed' && (result.reasonCode === 'card_declined' || result.reasonCode === 'payment_method_required' || result.reasonCode === 'wallet_authorization_required');
}

// planDetail drops a price name that repeats its product name ("Premium
// annual" under "Premium" reads as "annual").
function planDetail(product: string, price: string): string {
  const detail = price.trim();
  const name = product.trim();
  if (detail.toLowerCase() === name.toLowerCase()) {
    return '';
  }
  if (name && detail.toLowerCase().startsWith(name.toLowerCase() + ' ')) {
    return detail.slice(name.length).trim();
  }
  return detail;
}

function UpgradePlanName({ product, price }: { product: string; price: string }) {
  const detail = planDetail(product, price);
  return <span class="sm-upgrade-identity"><strong>{product}</strong>{detail && <span>{detail}</span>}</span>;
}

export function UpgradeStep({ source: initialSource, maskedEmail, onSwitchIdentity, refreshSource, products, initialPriceExternalId, onSelect, onSuccess, onClose, onDismiss, authMode, domainId }: Props) {
  useT();
  const { inline, successUrl } = useCheckoutPresentation();
  const [source, setSource] = useState(initialSource);
  const preselectedPrice = useRef(initialPriceExternalId);
  const pickerVisited = useRef(false);
  const [quote, setQuote] = useState<UpgradeQuote | null>(null);
  const [result, setResult] = useState<UpgradeResult | null>(null);
  const [reason, setReason] = useState(source.inProgress ? '' : source.blockedReason ?? '');
  const [loading, setLoading] = useState(false);
  const [providerMessage, setProviderMessage] = useState<string | undefined>(undefined);
  // Which genuine operation owns the busy lock; set and cleared with it.
  const [action, setAction] = useState<'confirm' | 'retry' | 'status' | null>(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [submittedId, setSubmittedId] = useState<string | null>(null);
  const [confirmationFailed, setConfirmationFailed] = useState(false);
  const [selectedPriceId, setSelectedPriceId] = useState(initialPriceExternalId);
  const quotedPriceId = useRef<string | undefined>(undefined);
  // Whether the backend reports payment details, learned from any quote. Kept
  // apart from the quote, which a failure clears; only 'legacy' may portal.
  const [paymentCapability, setPaymentCapability] = useState<'unknown' | 'inline' | 'legacy'>('unknown');
  const capabilityRef = useRef(paymentCapability);
  // Inline payment: editingCard expands the card form; setupIntentId is a card
  // already verified, sent with every confirm until the visitor switches away.
  const [editingCard, setEditingCard] = useState(false);
  const [cardFormKey, setCardFormKey] = useState(0);
  const [cardComplete, setCardComplete] = useState(false);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [verifying, setVerifying] = useState(false);
  const cardHandle = useRef<UpgradeCardHandle | null>(null);
  const paymentLabelId = `sm-upgrade-payment-${useId()}`;
  const identityLabelId = `sm-upgrade-identity-${useId()}`;
  const heading = useRef<HTMLHeadingElement>(null);
  const applied = result?.status === 'applied';
  useEffect(() => {
    heading.current?.focus({ preventScroll: true });
  }, [quote?.upgradeId, applied]);
  // An in-progress upgrade being resumed shows no plan list until it settles.
  const [resuming, setResuming] = useState(!!source.inProgress);
  const busy = useRef(false);
  const reverifyTried = useRef(false);
  const mounted = useRef(true);
  const closing = useRef(false);
  const [attemptRequests] = useState(() => createRequestScope());
  const phase = useRef<'idle' | 'sheet-open' | 'collecting' | 'submitting' | 'reconciling'>('idle');
  const [processingWallet, setProcessingWallet] = useState(false);
  const [renewalUncertain, setRenewalUncertain] = useState(!!initialSource.inProgress);
  const submittedWallet = useRef<{ upgradeId: string; convertsTrial: boolean; paymentMethodId: string } | null>(null);
  const outcomePending = useRef(!!initialSource.inProgress);
  const live = () => mounted.current && !closing.current;
  const invalidate = useCallback(() => {
    closing.current = true;
    attemptRequests.advance();
  }, []);
  const release = (owned: () => boolean) => {
    if (!owned() || !live()) {
      return;
    }
    busy.current = false;
    phase.current = outcomePending.current ? 'reconciling' : 'idle';
    setLoading(false);
    setAction(null);
    setAuthenticating(false);
    setVerifying(false);
    setProcessingWallet(false);
  };
  // Amounts come from the server's targets: they are in the subscription's
  // billing currency, which can differ from the price's base currency.
  const targets = products.flatMap((product) => product.prices.flatMap((price) => {
    const target = source.targets?.find((candidate) => candidate.priceExternalId === price.externalId);
    return target ? [{ product, price, target }] : [];
  }));
  // Resolved against the current targets every render, so a refreshed source
  // that dropped the highlighted price can never be reviewed with it.
  const selected = targets.find(({ price }) => price.externalId === selectedPriceId) ?? targets[0];
  const groups = targets.reduce<{ product: OnsiteProduct; entries: typeof targets }[]>((acc, entry) => {
    const group = acc.find((candidate) => candidate.product.externalId === entry.product.externalId);
    if (group) {
      group.entries.push(entry);
      return acc;
    }
    acc.push({ product: entry.product, entries: [entry] });
    return acc;
  }, []);

  const reportError = (err: unknown) => {
    logError('upgrade failed', err);
    const nextReason = errorReason(err);
    setReason(nextReason);
    setProviderMessage(undefined);
  };

  const adoptQuote = (next: UpgradeQuote) => {
    const capability = next.paymentMethod === undefined ? 'legacy' : 'inline';
    capabilityRef.current = capability;
    setPaymentCapability(capability);
    setQuote(next);
  };

  // The declined attempt is terminal; quote again for the same plan (never
  // confirming it) and open a fresh card form beside the kept summary.
  const requoteForNewCard = async (failed: UpgradeResult, priceId: string) => {
    setSetupIntentId(null);
    setEditingCard(true);
    setCardFormKey((value) => value + 1);
    setReason(failed.reasonCode ?? 'generic');
    setProviderMessage(failed.providerMessage);
    try {
      const fresh = await createUpgradeQuote(source.subscriptionId, priceId);
      if (!live()) {
        return;
      }
      if (fresh.paymentMethod === undefined) {
        // A resumed attempt on a backend without inline payment: keep the
        // plan list and portal recovery it always had.
        adoptQuote(fresh);
        setQuote(null);
        await reloadSource();
        return;
      }
      quotedPriceId.current = priceId;
      setSelectedPriceId(priceId);
      adoptQuote(fresh);
      setResult(null);
    } catch (err) {
      // intentionally tolerated: the decline stays shown and the plan list
      // (or its blocker) returns, as without inline payment.
      logError('upgrade re-quote after card failure failed', err);
      if (!live()) {
        return;
      }
      setQuote(null);
      await reloadSource();
    }
  };

  const adoptResult = async (next: UpgradeResult) => {
    if (!live()) {
      return;
    }
    setResult(next);
    if (isUpgradeTerminal(next.status)) {
      outcomePending.current = false;
      submittedWallet.current = null;
      clearPendingUpgrade();
      setResuming(false);
      setSubmittedId(null);
      setConfirmationFailed(false);
    }
    if (next.quote && (next.reasonCode === 'quote_changed' || next.reasonCode === 'quote_expired')) {
      adoptQuote(next.quote);
      setReason(next.reasonCode);
      return;
    }
    if (next.status === 'requires_action') {
      setPendingUpgrade(next.upgradeId);
      if (!next.clientSecret || !next.publishableKey) {
        throw new Error('Upgrade authentication response is missing Stripe credentials');
      }
      setAuthenticating(true);
      const stripe = await getStripe(next.publishableKey, next.providerAccount);
      if (!live()) {
        return;
      }
      const authenticated = await confirmUpgradePayment(stripe, next.upgradeId, next.clientSecret);
      if (!live()) {
        return;
      }
      setAuthenticating(false);
      // A still-pending authentication is retried only on an explicit click.
      setResult(authenticated);
      if (authenticated.status === 'requires_action' || authenticated.status === 'pending') {
        setReason('pending');
        return;
      }
      await adoptResult(authenticated);
      return;
    }
    if (next.status === 'applied') {
      setReason('');
      setProviderMessage(undefined);
      // Any of the products' prices: a resumed upgrade's source lists no targets.
      const product = products.find((candidate) => candidate.prices.some((price) => price.externalId === next.priceExternalId));
      onSuccess(next, product, product?.prices.find((price) => price.externalId === next.priceExternalId));
      return;
    }
    // The attempt's own price: a resumed upgrade has no quote on screen.
    const failedPriceId = next.priceExternalId ?? quotedPriceId.current;
    if (isCardFailure(next) && capabilityRef.current !== 'legacy' && failedPriceId) {
      await requoteForNewCard(next, failedPriceId);
      return;
    }
    if (isUpgradeTerminal(next.status)) {
      // A declined, expired or canceled attempt charged nothing. Re-read the
      // options: the plans come back for a retry, or a blocker (e.g. the
      // subscription now cancels) replaces them.
      setQuote(null);
      await reloadSource();
    }
    if (next.status === 'pending') {
      setPendingUpgrade(next.upgradeId);
    }
    setReason(next.reasonCode ?? (next.status === 'pending' ? 'pending' : 'generic'));
    setProviderMessage(next.providerMessage);
  };

  const reloadSource = async () => {
    try {
      const fresh = await refreshSource();
      if (!live()) {
        return;
      }
      // No eligible source left: nothing may stay selectable.
      setSource(fresh ?? { ...source, targets: [], inProgress: undefined });
    } catch (err) {
      // intentionally tolerated: the failure reason still shows and the modal
      // can be closed; reopening it re-reads the options.
      logError('upgrade options refresh failed', err);
    }
  };

  const run = async (operation: () => Promise<void>, kind: 'confirm' | 'retry' | 'status' | null = null) => {
    if (busy.current || !live()) {
      return;
    }
    busy.current = true;
    attemptRequests.advance();
    const owned = attemptRequests.capture();
    setLoading(true);
    setAction(kind);
    setReason('');
    setProviderMessage(undefined);
    try {
      await operation();
    } catch (err) {
      if (live()) {
        reportError(err);
      }
    } finally {
      release(owned);
    }
  };

  const selectSavedMethod = () => {
    if (busy.current || !live()) {
      return;
    }
    setSetupIntentId(null);
    setEditingCard(false);
  };

  const editNewCard = () => {
    if (busy.current || !live()) {
      return;
    }
    setSetupIntentId(null);
    setEditingCard(true);
    setCardFormKey((value) => value + 1);
  };

  // A soft session's quote hides the saved card. An ezAuth or identity token
  // proves the email without the visitor's help, so it is tried once, before
  // the first quote; the result only replaces the session if it is verified
  // and for the same visitor.
  const reverifySilently = async () => {
    reverifyTried.current = true;
    try {
      const credential = await getIdentityAdapter(authMode, domainId).resolveCredential();
      if (!live() || !credential || !('ezAuthToken' in credential || 'identityToken' in credential)) {
        return;
      }
      await establishSession(credential, live, undefined, { requireVerified: true, sameVisitor: true });
    } catch (err) {
      // intentionally tolerated: the quote still works; the visitor enters a new card.
      logError('upgrade silent re-verification failed', err);
    }
  };

  const selectTarget = (product: OnsiteProduct, price: OnsitePrice) => run(async () => {
    setSelectedPriceId(price.externalId);
    onSelect(product, price);
    if (!reverifyTried.current && !sessionVerified()) {
      await reverifySilently();
      if (!live()) {
        return;
      }
    }
    const next = await createUpgradeQuote(source.subscriptionId, price.externalId);
    if (live()) {
      quotedPriceId.current = price.externalId;
      adoptQuote(next);
      setResult(null);
    }
  });

  useEffect(() => {
    mounted.current = true;
    if (source.inProgress) {
      void run(async () => adoptResult(await getUpgradeStatus(source.inProgress!.upgradeId)));
      return () => { mounted.current = false; invalidate(); };
    }
    if (!source.blockedReason && initialPriceExternalId) {
      const target = targets.find(({ price }) => price.externalId === initialPriceExternalId);
      if (target) {
        void selectTarget(target.product, target.price);
      }
    }
    return () => { mounted.current = false; invalidate(); };
  }, []);

  const canConfirm = quote && !submittedId && (!result || result.reasonCode === 'quote_changed' || result.reasonCode === 'quote_expired');
  const inlinePayment = quote ? quote.paymentMethod !== undefined : paymentCapability !== 'legacy';
  // Only beside a confirmable quote: a declined attempt's form would bind to
  // a settled upgrade while its replacement quote loads.
  const needsAppleAuthorization = !!quote && quote.amountDueCents > 0 && quote.paymentMethod?.walletType === 'apple_pay';
  const cardFormOpen = !!canConfirm && inlinePayment && !setupIntentId && (editingCard || quote.paymentMethod === null || needsAppleAuthorization);
  // A submit settles the form state (submittedId, setupIntentId) mid-flight;
  // keep the clicked-from buttons in place, disabled, until it finishes.
  const lastPaymentActions = useRef({ change: false, saved: false });
  if (canConfirm && quote) {
    lastPaymentActions.current = {
      change: !cardFormOpen,
      saved: !!quote.paymentMethod && !needsAppleAuthorization && (cardFormOpen || !!setupIntentId),
    };
  }
  const paymentActions = lastPaymentActions.current;
  const walletCapable = !!quote && quote.amountDueCents > 0 && !!quote.publishableKey && !!quote.walletPaymentMethods?.some((method) => method === 'apple_pay' || method === 'google_pay');
  let paymentNoteKey = 'upgrade.paymentNote';
  if (cardFormOpen || setupIntentId) {
    paymentNoteKey = 'upgrade.payment.newCardNote';
  }
  // Nothing is charged at $0, saved card or new.
  if (quote?.amountDueCents === 0) {
    paymentNoteKey = 'upgrade.paymentNoteNoCharge';
  }

  const walletCurrent = (attempt: UpgradeWalletAttempt) => live() && attempt.isCurrent();
  const beginWallet = (upgradeId: string): UpgradeWalletAttempt | null => {
    if (busy.current || !live() || phase.current !== 'idle' || !canConfirm || quote.upgradeId !== upgradeId) {
      return null;
    }
    busy.current = true;
    phase.current = 'sheet-open';
    setLoading(true);
    setAction('confirm');
    setReason('');
    setProviderMessage(undefined);
    attemptRequests.advance();
    return { isCurrent: attemptRequests.capture(), upgradeId, convertsTrial: quote.convertsTrial };
  };
  const collectWallet = (attempt: UpgradeWalletAttempt) => {
    if (!walletCurrent(attempt) || phase.current !== 'sheet-open') {
      return false;
    }
    phase.current = 'collecting';
    setProcessingWallet(true);
    return true;
  };
  const releaseWallet = (attempt: UpgradeWalletAttempt) => {
    if (phase.current === 'sheet-open' || phase.current === 'collecting') {
      release(attempt.isCurrent);
    }
  };
  const submitWallet = async (attempt: UpgradeWalletAttempt, paymentMethodId: string) => {
    if (!walletCurrent(attempt) || phase.current !== 'collecting') {
      return;
    }
    phase.current = 'submitting';
    outcomePending.current = true;
    submittedWallet.current = { upgradeId: attempt.upgradeId, convertsTrial: attempt.convertsTrial, paymentMethodId };
    setRenewalUncertain(true);
    setSubmittedId(attempt.upgradeId);
    setConfirmationFailed(false);
    setPendingUpgrade(attempt.upgradeId);
    try {
      const next = await confirmUpgrade(attempt.upgradeId, attempt.convertsTrial, undefined, { paymentMethodId });
      if (walletCurrent(attempt)) {
        phase.current = 'reconciling';
        await adoptResult(next);
      }
    } catch (err) {
      if (walletCurrent(attempt)) {
        setConfirmationFailed(true);
        reportError(err);
      }
    } finally {
      release(attempt.isCurrent);
    }
  };

  const reviewCurrent = attemptRequests.capture();
  // Verifies a new card first (never charging), then confirms with it.
  const submit = (upgradeId: string, convertsTrial: boolean, kind: 'confirm' | 'retry' = 'confirm') => {
    if (!reviewCurrent()) {
      return;
    }
    return run(async () => {
      const owned = attemptRequests.capture();
      let verifiedSetup = setupIntentId;
      if (cardFormOpen) {
        if (!cardComplete) {
          return;
        }
        phase.current = 'collecting';
        const handle = cardHandle.current;
        if (!handle) {
          return;
        }
        setVerifying(true);
        verifiedSetup = await handle.verify();
        if (!live() || !owned()) {
          return;
        }
        setVerifying(false);
        if (!verifiedSetup) {
          return;
        }
        setSetupIntentId(verifiedSetup);
      }
      phase.current = 'submitting';
      outcomePending.current = true;
      submittedWallet.current = null;
      if (verifiedSetup) {
        setRenewalUncertain(true);
      }
      setSubmittedId(upgradeId);
      if (kind === 'confirm') {
        setConfirmationFailed(false);
      }
      setPendingUpgrade(upgradeId);
      try {
        const next = verifiedSetup ? await confirmUpgrade(upgradeId, convertsTrial, verifiedSetup) : await confirmUpgrade(upgradeId, convertsTrial);
        setConfirmationFailed(false);
        await adoptResult(next);
      } catch (err) {
        setConfirmationFailed(true);
        throw err;
      }
    }, kind);
  };

  const retryWallet = () => run(async () => {
    const tender = submittedWallet.current;
    if (!tender || tender.upgradeId !== submittedId) {
      return;
    }
    phase.current = 'submitting';
    setProcessingWallet(true);
    setPendingUpgrade(tender.upgradeId);
    try {
      await adoptResult(await confirmUpgrade(tender.upgradeId, tender.convertsTrial, undefined, { paymentMethodId: tender.paymentMethodId }));
    } catch (err) {
      setConfirmationFailed(true);
      throw err;
    }
  }, 'retry');

  const confirmClick = () => {
    if (!quote) {
      return;
    }
    void submit(quote.upgradeId, quote.convertsTrial);
  };
  // Confirm stays in place (spinner) while its own card or wallet submit is in flight.
  const submitting = action === 'confirm';
  const checkStatus = () => {
    const upgradeId = submittedId ?? result?.upgradeId ?? source.inProgress?.upgradeId;
    if (upgradeId) {
      void run(async () => {
        phase.current = 'reconciling';
        await adoptResult(await getUpgradeStatus(upgradeId));
      }, 'status');
    }
  };

  if (applied) {
    return (
      <CheckoutShell ariaLabel={t('upgrade.success')} currentStep={3} hideSteps onDismiss={onDismiss} onDismissRequested={invalidate}>
        <div class="sm-upgrade sm-upgrade-success">
          <div class="sm-upgrade-success-mark" aria-hidden="true">
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 4 4L19 6" /></svg>
          </div>
          <h2 class="sm-title sm-upgrade-title" ref={heading} tabIndex={-1}>{t('upgrade.success')}</h2>
          <p class="sm-upgrade-intro">{t('upgrade.successHint')}</p>
          {quote && <div class="sm-upgrade-success-plan"><UpgradePlanName product={quote.toProductName} price={quote.toPriceName} /></div>}
          {result.amountChargedCents !== undefined && <p class="sm-upgrade-receipt">{t('upgrade.charged', { amount: formatMinorUnits(result.amountChargedCents, quote?.currency ?? source.currency) })}</p>}
          {!!result.amountDeferredCents && <p class="sm-summary-note">{t('upgrade.deferredBalance', { amount: formatMinorUnits(result.amountDeferredCents, quote?.currency ?? source.currency) })}</p>}
          {inline ? (
            successUrl && <a class="sm-btn sm-btn-primary" href={successUrl}>{t('upgrade.continue')}</a>
          ) : (
            <button type="button" class="sm-btn sm-btn-primary" onClick={onClose}>{t('upgrade.continue')}</button>
          )}
        </div>
      </CheckoutShell>
    );
  }

  const awaitingOutcome = !!submittedId || result?.status === 'pending' || result?.status === 'requires_action' || (resuming && !result);
  const retryShown = (confirmationFailed || (!!submittedWallet.current && result?.status === 'pending')) && !!submittedId;
  const statusShown = !canConfirm && !submitting && awaitingOutcome;
  // The clicked button carries the spinner; when that button is gone (e.g. 3DS after a retry), show a bare one.
  const buttonBusy = submitting || (action === 'retry' && retryShown) || (action === 'status' && statusShown);
  const canChoose = !quote && !source.blockedReason && !resuming && !awaitingOutcome;
  if (canChoose && selected) {
    pickerVisited.current = true;
  }
  const canGoBack = !!quote && !!canConfirm && !preselectedPrice.current && pickerVisited.current && targets.length > 0 && !source.blockedReason && !resuming && !awaitingOutcome;
  const backToPicker = () => {
    if (!canGoBack || busy.current || !live() || phase.current !== 'idle' || outcomePending.current || !reviewCurrent()) {
      return;
    }
    attemptRequests.advance();
    setQuote(null);
    setResult(null);
    setReason('');
    setProviderMessage(undefined);
  };
  const switchIdentity = () => {
    if (!onSwitchIdentity || busy.current || !live() || phase.current !== 'idle' || outcomePending.current || awaitingOutcome) {
      return;
    }
    invalidate();
    onSwitchIdentity(selected?.product, selected?.price);
  };
  const title = quote ? t('upgrade.reviewTitle') : t('upgrade.title');
  const formatDay = (unix: number) => new Date(unix * 1000).toLocaleDateString(getLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
  const renewsAt = quote ? formatDay(quote.renewsAt) : '';
  const toDetail = quote ? planDetail(quote.toProductName, quote.toPriceName) : '';
  let paymentRowLabel = t('upgrade.payment.newCard');
  if (!cardFormOpen && quote?.paymentMethod) {
    paymentRowLabel = paymentMethodLabel(quote.paymentMethod);
  }
  if (setupIntentId) {
    paymentRowLabel = t('upgrade.payment.newCardAdded');
  }
  if (needsAppleAuthorization && !setupIntentId) {
    paymentRowLabel = t('upgrade.payment.applePayAuthorize');
  }
  const summary = quote && <UpgradeSummary quote={quote} detail={toDetail} renewsAt={renewsAt} />;
  return (
    // Picker and review are separate steps: a new shell replays the entrance
    // animation, as normal checkout does between its step components.
    <CheckoutShell
      key={quote ? 'review' : 'picker'}
      ariaLabel={title}
      currentStep={2}
      sidebar={summary}
      wide
      hideSteps
      title={quote ? <h2 class="sm-title sm-upgrade-title" ref={heading} tabIndex={-1}>{title}</h2> : undefined}
      onBack={canGoBack ? backToPicker : undefined}
      backDisabled={loading || authenticating || verifying || processingWallet}
      onDismiss={onDismiss}
      onDismissRequested={invalidate}
    >
      <div class={`sm-upgrade${quote ? ' sm-upgrade-reviewing' : ''}`}>
        <div class="sm-upgrade-meta">
          {quote ? (
            <p class="sm-upgrade-intro sm-upgrade-starts">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M13 2 4 14h7l-1 8 9-12h-7l1-8Z" /></svg>
              {t('upgrade.reviewHint')}
            </p>
          ) : (
            <header class="sm-upgrade-header">
              <h2 class="sm-title sm-upgrade-title" ref={heading} tabIndex={-1}>{title}</h2>
              {canChoose && <p class="sm-upgrade-intro">{t('upgrade.chooseHint')}</p>}
            </header>
          )}
          {maskedEmail && !quote && (
            <p class="sm-identity-note">
              {t('paywall.purchasingAs')} <strong>{maskedEmail}</strong>
              {onSwitchIdentity && <>{' · '}<button type="button" class="sm-identity-change" disabled={loading || authenticating || verifying || processingWallet || awaitingOutcome} onClick={switchIdentity}>{t('paywall.notYou')}</button></>}
            </p>
          )}
        </div>
        {!quote && <div class="sm-upgrade-current">
          <span class="sm-upgrade-current-label">{t('upgrade.current')}</span>
          <div class="sm-upgrade-current-row">
            <UpgradePlanName product={source.productName} price={source.priceName} />
            <span class="sm-upgrade-price notranslate"><strong>{formatMinorUnits(source.amountCents, source.currency)}</strong><span>{cadenceLabel(source.billingInterval, source.billingIntervalCount)}</span></span>
          </div>
          {source.trialEndsAt !== undefined && <p class="sm-upgrade-trial-ends">{t('upgrade.trialCurrent', { date: formatDay(source.trialEndsAt) })}</p>}
        </div>}
        {canChoose && selected && (
          <div class="sm-plan-list" role="radiogroup" aria-label={t('upgrade.options')} aria-busy={loading}>
            {groups.map(({ product, entries }) => (
              <div class="sm-plan" key={product.externalId}>
                <h3 class="sm-plan-name">{product.name}</h3>
                {product.description && <p class="sm-plan-desc">{product.description}</p>}
                {/* Target amounts are in the subscription's billing currency. */}
                {entries.map(({ price, target }) => (
                  <PriceOption key={price.externalId} price={{ ...price, amountCents: target.amountCents, currency: target.currency }} trial={false} selected={selected.price.externalId === price.externalId} onSelect={() => setSelectedPriceId(price.externalId)} disabled={loading} />
                ))}
              </div>
            ))}
          </div>
        )}
        {quote && (maskedEmail || inlinePayment) && (
          <div class="sm-upgrade-details">
            {maskedEmail && (
              <section class="sm-upgrade-detail sm-identity-note" aria-labelledby={identityLabelId}>
                <span class="sm-upgrade-detail-label" id={identityLabelId}>{t('paywall.purchasingAs')}</span>
                <span class="sm-upgrade-detail-value">
                  <span class="sm-upgrade-avatar" aria-hidden="true">{maskedEmail.charAt(0).toUpperCase()}</span>
                  <strong class="notranslate">{maskedEmail}</strong>
                </span>
                {onSwitchIdentity && <button type="button" class="sm-upgrade-change sm-identity-change" disabled={loading || authenticating || verifying || processingWallet || awaitingOutcome} onClick={switchIdentity}>{t('paywall.notYou')}</button>}
              </section>
            )}
            {inlinePayment && (
              <section class="sm-upgrade-detail sm-upgrade-payment" aria-labelledby={paymentLabelId}>
                <span class="sm-upgrade-detail-label" id={paymentLabelId}>{t('upgrade.payment.label')}</span>
                <span class="sm-upgrade-detail-value">
                  <span class="sm-upgrade-payment-icon" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="2.5" y="5" width="19" height="14" rx="2.5" /><path d="M2.5 10h19M6.5 15h3" /></svg></span>
                  <span class="sm-upgrade-payment-method notranslate">{paymentRowLabel}</span>
                </span>
                {(canConfirm || submitting) && (paymentActions.change || paymentActions.saved) && (
                  <span class="sm-upgrade-payment-actions">
                    {paymentActions.change && <button type="button" class="sm-upgrade-change" disabled={loading} onClick={editNewCard}>{t('upgrade.payment.change')}</button>}
                    {paymentActions.saved && <button type="button" class="sm-upgrade-change" disabled={loading} onClick={selectSavedMethod}>{t('upgrade.payment.useSaved')}</button>}
                  </span>
                )}
              </section>
            )}
          </div>
        )}
        {quote && inlinePayment && (cardFormOpen || setupIntentId) && (
          <div class="sm-upgrade-payment-entry">
            {cardFormOpen && walletCapable && <UpgradeWalletForm key={quote.upgradeId} quote={quote} disabled={loading} onBegin={beginWallet} onCollect={collectWallet} isCurrent={walletCurrent} onPaymentMethod={submitWallet} onRelease={releaseWallet} />}
            {cardFormOpen && <UpgradePaymentForm key={cardFormKey} upgradeId={quote.upgradeId} disabled={loading} autoFocus={!walletCapable} handleRef={cardHandle} onCompleteChange={setCardComplete} />}
          </div>
        )}
        {renewalUncertain && <p class="sm-summary-note">{t('upgrade.payment.renewalUncertain')}</p>}
        {reason && (reason === 'pending' || reason === 'quote_changed' || reason === 'quote_expired'
          ? <p role="alert" class="sm-upgrade-notice">{reasonMessage(reason, inlinePayment)}</p>
          : <p role="alert" class="sm-error">{providerMessage ?? reasonMessage(reason, inlinePayment)}</p>)}
        {!inlinePayment && (reason === 'card_declined' || reason === 'payment_method_required') && <button type="button" class="sm-btn sm-btn-secondary" onClick={() => void openSubscriberPortal()}>{t('upgrade.updatePayment')}</button>}
        {loading && !buttonBusy && !canChoose && <p role="status" class="sm-upgrade-status" aria-label={t('upgrade.loading')}><span class="sm-btn-spinner" aria-hidden="true" /></p>}
        {canChoose && selected && <div class="sm-upgrade-actions">
          <button type="button" class="sm-btn sm-btn-primary" disabled={loading} aria-busy={loading} onClick={() => void selectTarget(selected.product, selected.price)}>{loading && <span class="sm-btn-spinner" aria-hidden="true" />}{t('upgrade.continue')}</button>
        </div>}
        {quote && (canConfirm || submitting) && <div class="sm-upgrade-actions">
          <button type="button" class="sm-btn sm-btn-primary" disabled={loading || (cardFormOpen && !cardComplete)} aria-busy={submitting} onClick={confirmClick}>{submitting && <span class="sm-btn-spinner" aria-hidden="true" />}{t('upgrade.confirmAmount', { amount: formatMinorUnits(quote.amountDueCents, quote.currency) })}</button>
          <p class="sm-upgrade-payment-note"><svg width="12" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><rect x="4" y="10" width="16" height="11" rx="2" /><path d="M8 10V6a4 4 0 0 1 8 0v4" /></svg>{t(paymentNoteKey)}</p>
        </div>}
        {retryShown && <button type="button" class="sm-btn sm-btn-secondary" disabled={loading} aria-busy={action === 'retry'} onClick={submittedWallet.current ? () => void retryWallet() : () => void submit(submittedId, quote?.upgradeId === submittedId && quote.convertsTrial, 'retry')}>{action === 'retry' && <span class="sm-btn-spinner" aria-hidden="true" />}{t('checkout.tryAgain')}</button>}
        {statusShown && <button type="button" class="sm-btn sm-btn-primary" disabled={loading} aria-busy={action === 'status'} onClick={checkStatus}>{action === 'status' && <span class="sm-btn-spinner" aria-hidden="true" />}{t('paymentForm.checkStatus')}</button>}
      </div>
    </CheckoutShell>
  );
}
